// Layer 0 — structural invariants.
//
// Unlike the Layer 2 plausibility checks, which observe a completed parse and
// advise the user, these assert that the parse itself is structurally sound. A
// fatal violation REFUSES the file. That is the point: every bug in the National
// Grid incident (August 2026) was the parser guessing, never checking the guess,
// and then producing a confident wrong answer. A refused file is a support
// ticket; a wrong peak demand on a permit application is not recoverable.
//
// Rule: no diagnostic may explain away a violated invariant. These run BEFORE
// the data-quality disclosures, so a collapse cannot be narrated as a
// daylight-saving artifact on its way out the door.

import type { ParsedDataPoint } from './types';

// ─── Tunable thresholds ──────────────────────────────────────────────────────
/** Meter data older than this is not a real interval export. */
const MIN_PLAUSIBLE_YEAR = 1990;
/** Allow next year for exports that run past a year boundary or carry a TZ shift. */
const FUTURE_YEAR_ALLOWANCE = 1;
/** Above this share of out-of-window timestamps the misparse is systematic. */
const MAX_OUT_OF_WINDOW_FRACTION = 0.05;
/** Below this share of distinct timestamps the file did not parse as rows at all. */
const MIN_DISTINCT_TIMESTAMP_FATAL = 0.5;
/** Below this share it still warrants a look, but is not proof of a parse failure. */
const MIN_DISTINCT_TIMESTAMP_SUSPECT = 0.99;
/** Collapse ratios are meaningless on a handful of rows. */
const MIN_ROWS_FOR_COLLAPSE_CHECK = 20;

export interface InvariantViolation {
  check: string;
  fatal: boolean;
  message: string;
  detail: Record<string, unknown>;
}

function modeOf(counts: number[]): number | null {
  if (counts.length === 0) return null;
  const tally = new Map<number, number>();
  for (const c of counts) tally.set(c, (tally.get(c) ?? 0) + 1);
  let best: number | null = null;
  let bestCount = 0;
  for (const [value, count] of tally) {
    if (count > bestCount) { bestCount = count; best = value; }
  }
  return best;
}

/**
 * A well-formed delimited file has one field count. When the typical data row
 * carries MORE fields than the header, a delimiter leaked out of a value and
 * every column to its right is shifted — which is exactly how a ZIP code ended
 * up in the `interval_start` position and parsed as the year 12061.
 *
 * Fewer fields than the header is the benign direction (exports routinely omit
 * trailing empty columns), so that is reported without refusing the file.
 *
 * Text-delimited input only. The spreadsheet reader returns ragged arrays by
 * design — trailing empty cells are trimmed per row — so field counts there
 * carry no structural meaning.
 */
export function checkRowShape(
  headerRow: (string | null)[] | null,
  dataRows: unknown[][]
): InvariantViolation | null {
  if (!headerRow || dataRows.length === 0) return null;

  const headerCount = headerRow.length;
  const modalCount = modeOf(dataRows.map(r => (r as unknown[]).length));
  if (modalCount === null || modalCount === headerCount) return null;

  if (modalCount > headerCount) {
    return {
      check: 'row_shape_overflow',
      fatal: true,
      message:
        `This file could not be read reliably: its data rows contain ${modalCount} fields but the header row ` +
        `defines ${headerCount} columns. That usually means a value contains the column separator without being ` +
        'quoted, which shifts every column after it. Please re-export the file, or contact support with a copy.',
      detail: { headerCount, modalCount, rowCount: dataRows.length },
    };
  }

  return {
    check: 'row_shape_underflow',
    fatal: false,
    message:
      `Data rows carry ${modalCount} fields where the header defines ${headerCount} columns. ` +
      'Trailing columns may have been omitted by the export.',
    detail: { headerCount, modalCount, rowCount: dataRows.length },
  };
}

/**
 * Every timestamp must land in a window a meter reading could plausibly occupy.
 *
 * A misparse does not produce a slightly-off date, it produces an absurd one:
 * the bare string "12061" read as a year put every reading in the year 12061,
 * which travelled all the way to Postgres and failed there as a timestamp range
 * error. Catching it here turns a database error into a clear refusal.
 *
 * A handful of out-of-window rows is corrupt data and gets dropped; a large
 * share means the timestamp column itself was misread, and the file is refused.
 */
export function checkTimestampWindow(points: ParsedDataPoint[]): {
  violation: InvariantViolation | null;
  kept: ParsedDataPoint[];
  droppedCount: number;
} {
  if (points.length === 0) return { violation: null, kept: points, droppedCount: 0 };

  const maxYear = new Date().getFullYear() + FUTURE_YEAR_ALLOWANCE;
  const kept: ParsedDataPoint[] = [];
  let droppedCount = 0;
  let sampleBad: string | null = null;

  for (const p of points) {
    const year = new Date(p.timestamp).getUTCFullYear();
    if (Number.isFinite(year) && year >= MIN_PLAUSIBLE_YEAR && year <= maxYear) {
      kept.push(p);
    } else {
      droppedCount++;
      if (sampleBad === null) sampleBad = p.timestamp;
    }
  }

  if (droppedCount === 0) return { violation: null, kept, droppedCount: 0 };

  const fraction = droppedCount / points.length;
  const detail = {
    droppedCount,
    totalCount: points.length,
    fractionPercent: Math.round(fraction * 1000) / 10,
    windowStartYear: MIN_PLAUSIBLE_YEAR,
    windowEndYear: maxYear,
    exampleTimestamp: sampleBad,
  };

  if (kept.length === 0 || fraction > MAX_OUT_OF_WINDOW_FRACTION) {
    return {
      violation: {
        check: 'timestamp_out_of_window',
        fatal: true,
        message:
          `The date column could not be read: ${droppedCount} of ${points.length} readings resolve to a date outside ` +
          `${MIN_PLAUSIBLE_YEAR}–${maxYear} (for example ${sampleBad}). TrueLoad did not use this file rather than ` +
          'calculate from dates it cannot trust. Please confirm the export contains a date and time column.',
        detail,
      },
      kept,
      droppedCount,
    };
  }

  return {
    violation: {
      check: 'timestamp_out_of_window',
      fatal: false,
      message:
        `${droppedCount} reading(s) carried a date outside ${MIN_PLAUSIBLE_YEAR}–${maxYear} and were excluded.`,
      detail,
    },
    kept,
    droppedCount,
  };
}

/**
 * Distinct timestamps must account for most of the rows.
 *
 * When 53,062 readings resolved to one timestamp, the parser did notice — and
 * then described it in the AHJ disclosure as a daylight-saving artifact. Rows
 * collapsing wholesale is a parse failure, and no amount of repeated-timestamp
 * prose makes it otherwise. Genuine DST fall-back repeats one hour per year,
 * which is a rounding error against this threshold.
 */
export function checkTimestampCollapse(points: ParsedDataPoint[]): InvariantViolation | null {
  if (points.length < MIN_ROWS_FOR_COLLAPSE_CHECK) return null;

  const distinct = new Set(points.map(p => p.timestamp)).size;
  const ratio = distinct / points.length;
  if (ratio >= MIN_DISTINCT_TIMESTAMP_SUSPECT) return null;

  const detail = {
    distinctCount: distinct,
    totalCount: points.length,
    ratioPercent: Math.round(ratio * 1000) / 10,
  };

  if (ratio < MIN_DISTINCT_TIMESTAMP_FATAL) {
    return {
      check: 'timestamp_collapse',
      fatal: true,
      message:
        `This file could not be read reliably: ${points.length} readings resolve to only ${distinct} distinct ` +
        'date/times. That indicates the date column was not read correctly rather than a genuine repeat. ' +
        'Please confirm the export contains one row per interval, or contact support with a copy.',
      detail,
    };
  }

  return {
    check: 'timestamp_collapse',
    fatal: false,
    message:
      `${points.length - distinct} of ${points.length} readings repeat a date/time already seen in this file. ` +
      'Please confirm the export contains one row per interval.',
    detail,
  };
}

// ─── Day-per-row (wide) layout ───────────────────────────────────────────────
/** A header needs this many time-slot columns to be a day's readings laid across. */
const MIN_SLOT_COLUMNS = 12;
/** How far down the sheet to look for that header, preamble included. */
const WIDE_SCAN_ROWS = 40;
const SLOT_CLOCK_RE = /^\d{1,2}:\d{2}(?::\d{2})?\s*(?:[ap]\.?m\.?)?$/i;
const SLOT_RANGE_RE = /^\d{1,2}:\d{2}.*[-–]\s*\d{1,2}:\d{2}/;
const SLOT_INDEX_RE = /^(?:he|hr|hour|int|interval|period|kwh|kw)\s*[-_#.]?\s*\d{1,3}$/i;
/** Day zero of the 1900 and 1904 date systems: where a time-typed header cell lands. */
const EXCEL_DAY_ZERO = [Date.UTC(1899, 11, 30), Date.UTC(1904, 0, 1)];

function isSlotLabel(cell: unknown): boolean {
  if (cell instanceof Date) {
    const t = cell.getTime();
    return !isNaN(t) && EXCEL_DAY_ZERO.includes(Math.floor(t / 86400000) * 86400000);
  }
  if (typeof cell !== 'string') return false;
  const s = cell.trim();
  return SLOT_CLOCK_RE.test(s) || SLOT_RANGE_RE.test(s) || SLOT_INDEX_RE.test(s);
}

/** Longest run of cells reading 0,1,2… or 1,2,3… — an hour or interval number header. */
function longestCountingRun(row: unknown[]): number {
  let best = 0;
  let run = 0;
  let prev: number | null = null;
  for (const cell of row) {
    const text = typeof cell === 'string' ? cell.trim() : null;
    const n = typeof cell === 'number' && Number.isInteger(cell) ? cell
      : text !== null && /^\d{1,3}$/.test(text) ? Number(text)
      : null;
    if (n !== null && prev !== null && n === prev + 1) {
      run++;
      prev = n;
    } else if (n === 0 || n === 1) {
      run = 1;
      prev = n;
    } else {
      run = 0;
      prev = null;
    }
    best = Math.max(best, run);
  }
  return best;
}

function numericCellCount(row: unknown[]): number {
  let count = 0;
  for (const cell of row) {
    if (typeof cell === 'number' && Number.isFinite(cell)) count++;
    else if (typeof cell === 'string' && cell.trim() !== '' && Number.isFinite(Number(cell.replace(/,/g, '')))) count++;
  }
  return count;
}

/**
 * Some utilities export one row per day with that day's readings laid across
 * the columns: Date, 00:15, 00:30 … 24:00 (or HE1 … HE24, or 1 … 96). The
 * parser reads one timestamp column and one value column, so it took the date
 * as the timestamp and a single quarter-hour column as the day's energy, and
 * divided by 24. A synthetic year in that layout came out at 0.09 kW against a
 * true 8.6 kW (2026-10-21) — a hundredfold understatement in the direction that
 * frees capacity, with only a "low peak" banner to say anything was wrong.
 *
 * layout.ts reads the layout. This is the net under it: a header row with many
 * time-slot labels above rows holding as many numbers, which the reader could
 * not turn into instructions, is refused by name rather than read as ordinary
 * rows. An ordinary file — one clock column at most — never trips it.
 */
export function checkWideDayLayout(rows: unknown[][]): InvariantViolation | null {
  const scan = rows.slice(0, WIDE_SCAN_ROWS);
  for (let r = 0; r < scan.length; r++) {
    const row = (scan[r] ?? []) as unknown[];
    const slotCount = Math.max(row.filter(isSlotLabel).length, longestCountingRun(row));
    if (slotCount < MIN_SLOT_COLUMNS) continue;
    const below = rows.slice(r + 1, r + 6) as unknown[][];
    if (!below.some(b => numericCellCount(b ?? []) >= MIN_SLOT_COLUMNS)) continue;
    return {
      check: 'wide_day_layout',
      fatal: true,
      message:
        `This file has one row per day, with that day's readings spread across ${slotCount} columns, but ` +
        'TrueLoad could not find the column that gives each row its date, so the file was not used. Reading it as ' +
        'an ordinary file would report a peak far below the real one. Please download the interval data with one ' +
        'reading per row (often called the Green Button or interval data download), or email this file to ' +
        'support@trueloadapp.com.',
      detail: { headerRowIndex: r, slotCount },
    };
  }
  return null;
}
